import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { Bar } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import {
    Package,
    AlertTriangle,
    Ship,
    Anchor,
    FileCheck2,
    CalendarX,
    FileWarning,
    Timer,
    TrendingDown,
    CheckCircle2,
} from "lucide-react";
import FileStatusDrawer from "./FileStatusDrawer";
import { TRANSPORT_MODES } from "./transportModes";
import {
    UI,
    toneColor,
    priorityTone,
    safeNumber,
    formatMoney,
    shortLabel,
    fetchExchangeRates,
    convertCurrency,
    KpiCard,
    KpiGrid,
    Panel,
    ActionList,
    RankList,
    DonutWithLegend,
    MetricRow,
    SegmentedTabs,
    DashboardToolbar,
    barChartOptions,
    TruncatedText,
    StackedCell,
    DetailsButton,
    TableCard,
    withRawSort,
    dateValue,
    useDateRange,
    downloadCsv,
    csvDate,
    csvMoney,
    PRIORITY_RANK,
    ProgressStat,
    TimelineList,
    relativeDays,
} from "./oceanDashboardUi";

const safeText = (value, fallback = "—") => {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "string" || typeof value === "number") return String(value);

    if (Array.isArray(value)) {
        return value.map((item) => safeText(item, "")).filter(Boolean).join(", ");
    }

    if (typeof value === "object") {
        return value.companyName || value.name || value.label || value.title || value.value || fallback;
    }

    return fallback;
};

const STAGE_TONES = {
    "Booking Pending": "danger",
    "Waiting Departure": "warning",
    "In Transit": "info",
    "Delivered / POD Pending": "teal",
    "Ready to Close": "success",
    Open: "neutral",
};

const STAGE_ORDER = Object.keys(STAGE_TONES);

const BREAKDOWNS = [
    { value: "customer", label: "Customers" },
    { value: "shipper", label: "Shippers" },
    { value: "consignee", label: "Consignees" },
    { value: "shippingLine", label: "Shipping Lines" },
    { value: "service", label: "Service" },
    { value: "freightStatus", label: "Freight Status" },
    { value: "type", label: "Type" },
    { value: "priority", label: "Priority" },
    { value: "docHealth", label: "Doc Health" },
];

function HflDashboard({ mode = "ocean" }) {
    const cfg = TRANSPORT_MODES[mode];
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [currency, setCurrency] = useState("EUR");
    const [exchangeRates, setExchangeRates] = useState(undefined);
    const { dateRange, setDateRange, inRange } = useDateRange();
    const [tableFilters, setTableFilters] = useState([]);
    const [breakdown, setBreakdown] = useState("customer");
    const [selectedId, setSelectedId] = useState(null);
    const navigate = useNavigate();

    const clickEditHandler = (id) => {
        navigate(`${cfg.hfl.editPath}?id=${id}`);
    };
    const tableRef = React.useRef(null);

    const filterTitleMap = {
        service: "Service Level",
        freightStatus: "Freight Status",
        shipper: "Shipper",
        consignee: "Consignee",
        customer: "Customer",
        shippingLine: "Shipping Line",
        type: "HFL Type",
        docHealth: "Document Health",
        stage: "Current Stage",
        priority: "Priority",
        actionRequired: "Action Required",
        pendingBookings: "Pending Bookings",
        missingDocuments: "Missing Documents",
        departingSoon: "Departing Soon",
        arrivingSoon: "Arriving Soon",
        readyToClose: "Ready to Close",
        demurrageRisk: "Demurrage Risk",
        detentionRisk: "Detention Risk",
        negativeMargin: "Negative Margin",
    };

    const setFilter = (type, value, label) => {
        const title = filterTitleMap[type] || "Filter";

        const newFilter = {
            type,
            value: value ?? null,
            label: value ? `${title}: ${label || value}` : title,
        };

        setTableFilters((prev) => {
            const exists = prev.some((f) => f.type === newFilter.type && f.value === newFilter.value);

            if (exists) {
                return prev.filter((f) => !(f.type === newFilter.type && f.value === newFilter.value));
            }

            return [...prev, newFilter];
        });

        setTimeout(() => {
            tableRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }, 150);
    };

    const isFilterActive = (type, value = null) =>
        tableFilters.some((f) => f.type === type && (value === null || f.value === value));

    const activeFilterValue = (type) => tableFilters.find((f) => f.type === type)?.value;

    const removeFilter = (filter) => {
        setTableFilters((prev) =>
            prev.filter((f) => !(f.type === filter.type && f.value === filter.value))
        );
    };

    const clearFilter = () => {
        setTableFilters([]);
    };

    useEffect(() => {
        const loadRates = async () => {
            const data = await fetchExchangeRates();
            setExchangeRates(data);
        };

        loadRates();
    }, []);

    useEffect(() => {
        const fetchHfl = async () => {
            try {
                const res = await cfg.hfl.fetch();
                const raw = res?.data?.data || res?.data || [];

                const normalized = raw.map((item) => {
                    const a = item.attributes || item;
                    const cargoDetails = Array.isArray(a.cargoDetails) ? a.cargoDetails : [];

                    // Each cost keeps its own currency (e.g. TRY pickup on a EUR file); they are
                    // converted one by one, never added up as if they were the selling currency.
                    const sellingCurrency = a.sellingRateCurrency || currency;
                    const buyingParts = [
                        [a.buyingRate, a.buyingRateCurrency],
                        [a.pickupRate, a.pickupRateCurrency],
                        [a.deliveryRate, a.deliveryRateCurrency],
                        [a.exportCustomsRate, a.exportCustomsRateCurrency],
                        [a.importCustomsRate, a.importCustomsRateCurrency],
                    ]
                        .map(([amount, partCurrency]) => ({
                            amount: safeNumber(amount),
                            currency: partCurrency || sellingCurrency,
                        }))
                        .filter((part) => part.amount);

                    const selling = safeNumber(a.finalSellingRate ?? a.sellingRate);

                    const containerNo =
                        a.plateContainerNumber ||
                        a.containerNo ||
                        cargoDetails.find((c) => c?.containerNo)?.containerNo ||
                        "—";

                    return {
                        id: item.id || item._id,
                        hflNo: a.shipmentNo ? `${cfg.hfl.prefix}${a.shipmentNo}` : "—",
                        customer: safeText(a.customer),
                        shipper: safeText(a.shipper),
                        consignee: safeText(a.consignee),
                        type: a[cfg.hfl.typeField] || "Unknown",
                        service: a.service || "Unknown",
                        freightStatus: a.freightStatus || "Unknown",
                        pol: a.vesselPol?.port || a.pickupAddress1?.port || "—",
                        pod: a.vesselPod?.port || a.deliveryAddress2?.port || "—",
                        polCountry: a.vesselPol?.country || a.pickupAddress1?.country || "—",
                        podCountry: a.vesselPod?.country || a.deliveryAddress2?.country || "—",
                        shippingLine: a.shippingLine || "—",
                        vesselName: a.vesselName || "—",
                        etd: a.estimatedDeparture,
                        eta: a.estimatedDeliveryDate,
                        actualDeparture: a.actualDeparture,
                        actualDelivery: a.actualDelivery,
                        proofOfDelivery: a.proofOfDelivery,
                        bookingConfirmationPdf: a.bookingConfirmationPdf,
                        commercialInvoice: a.commercialInvoice,
                        packingList: a.packingList,
                        exportDeclaration: a.exportDeclaration,
                        importDeclaration: a.importDeclaration,
                        draftBl: a.draftBl,
                        freeTimeDemurrage: a.freeTimeDemurrage,
                        freeTimeDetention: a.freeTimeDetention,
                        containerNo,
                        selling,
                        buyingParts,
                        currency: sellingCurrency,
                        cargoCount: cargoDetails.length,
                        grossWeight: cargoDetails.reduce((sum, c) => sum + safeNumber(c.grossWeight), 0),
                        cbm: safeNumber(a.totals?.totalCbm),
                    };
                });

                setRows(normalized);
            } catch (error) {
                console.error("HFL dashboard fetch error:", error);
                setRows([]);
            } finally {
                setLoading(false);
            }
        };

        fetchHfl();
    }, []);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();

        const dated = rows.filter((row) => inRange(row.etd));
        if (!q) return dated;

        return dated.filter((row) =>
            [
                row.hflNo,
                row.customer,
                row.shipper,
                row.consignee,
                row.type,
                row.service,
                row.freightStatus,
                row.pol,
                row.pod,
                row.shippingLine,
                row.vesselName,
                row.containerNo,
            ]
                .join(" ")
                .toLowerCase()
                .includes(q)
        );
    }, [rows, search, dateRange]);

    // Dosya alanları dizi olarak saklanabilir (ör. []); boş dizi de "eksik" sayılmalı
    const isDocMissing = (value) => (Array.isArray(value) ? value.length === 0 : !value);
    const hasDoc = (value) => !isDocMissing(value);

    const getMissingDocs = (row) => {
        const missing = [];

        if (isDocMissing(row.bookingConfirmationPdf)) missing.push("Booking Confirmation");
        if (isDocMissing(row.commercialInvoice)) missing.push("Commercial Invoice");
        if (isDocMissing(row.packingList)) missing.push("Packing List");
        if (isDocMissing(row.exportDeclaration)) missing.push("Export Declaration");
        if (isDocMissing(row.importDeclaration)) missing.push("Import Declaration");

        return missing;
    };

    const getDocumentHealth = (row) => {
        const requiredDocs = [
            row.bookingConfirmationPdf,
            row.commercialInvoice,
            row.packingList,
            row.exportDeclaration,
            row.importDeclaration,
        ];

        const completed = requiredDocs.filter(hasDoc).length;
        return Math.round((completed / requiredDocs.length) * 100);
    };

    const getDocumentHealthBucket = (row) => {
        const health = getDocumentHealth(row);

        if (health <= 40) return "0-40% Critical";
        if (health <= 70) return "41-70% Needs Action";
        if (health < 100) return "71-99% Almost Complete";
        return "100% Complete";
    };

    const getCurrentStage = (row) => {
        if (!row.bookingConfirmationPdf) return "Booking Pending";
        if (!row.actualDeparture) return "Waiting Departure";
        if (row.actualDeparture && !row.actualDelivery) return "In Transit";
        if (row.actualDelivery && !row.proofOfDelivery) return "Delivered / POD Pending";
        if (row.proofOfDelivery) return "Ready to Close";

        return "Open";
    };

    const getFreeTimeRisk = (row) => {
        const today = dayjs();

        const demurrageDaysLeft =
            row.eta && row.freeTimeDemurrage
                ? dayjs(row.eta).add(Number(row.freeTimeDemurrage), "day").diff(today, "day")
                : null;

        const detentionDaysLeft =
            row.eta && row.freeTimeDetention
                ? dayjs(row.eta).add(Number(row.freeTimeDetention), "day").diff(today, "day")
                : null;

        if (demurrageDaysLeft !== null && demurrageDaysLeft <= 2) {
            return `Demurrage risk: ${demurrageDaysLeft} day left`;
        }

        if (detentionDaysLeft !== null && detentionDaysLeft <= 2) {
            return `Detention risk: ${detentionDaysLeft} day left`;
        }

        return "No Risk";
    };

    const getDetailedPriority = (row) => {
        const missingDocs = getMissingDocs(row);
        const freeTimeRisk = getFreeTimeRisk(row);

        if (!row.bookingConfirmationPdf) return "Critical";
        if (freeTimeRisk !== "No Risk") return "Critical";
        if (missingDocs.length >= 2) return "High";
        if (missingDocs.length === 1) return "Medium";

        return "Normal";
    };

    const isDepartingSoon = (row) =>
        row.etd && dayjs(row.etd).diff(dayjs(), "day") >= 0 && dayjs(row.etd).diff(dayjs(), "day") <= 3;

    const isArrivingSoon = (row) =>
        row.eta && dayjs(row.eta).diff(dayjs(), "day") >= 0 && dayjs(row.eta).diff(dayjs(), "day") <= 5;

    // KPI kartındaki "Action Required" sayısı ile aynı kural (önceden yalnızca Critical filtreleniyordu)
    const isActionRequired = (row) =>
        getDetailedPriority(row) !== "Normal" ||
        getCurrentStage(row) === "Ready to Close" ||
        isDepartingSoon(row) ||
        isArrivingSoon(row);

    const getConvertedSelling = (row) =>
        convertCurrency(row.selling, row.currency, currency, exchangeRates);

    const getConvertedBuying = (row) =>
        (row.buyingParts || []).reduce(
            (sum, part) => sum + convertCurrency(part.amount, part.currency, currency, exchangeRates),
            0
        );

    const getConvertedProfit = (row) => getConvertedSelling(row) - getConvertedBuying(row);

    const applyTableFilter = (data) => {
        if (!tableFilters.length) return data;

        return data.filter((row) =>
            tableFilters.every((filter) => {
                switch (filter.type) {
                    case "actionRequired":
                        return isActionRequired(row);

                    case "pendingBookings":
                        return !row.bookingConfirmationPdf;

                    case "missingDocuments":
                        return getMissingDocs(row).length > 0;

                    case "departingSoon":
                        return isDepartingSoon(row);

                    case "arrivingSoon":
                        return isArrivingSoon(row);

                    case "readyToClose":
                        return getCurrentStage(row) === "Ready to Close";

                    case "demurrageRisk":
                        return getFreeTimeRisk(row).includes("Demurrage");

                    case "detentionRisk":
                        return getFreeTimeRisk(row).includes("Detention");

                    case "negativeMargin":
                        return getConvertedProfit(row) < 0;

                    case "priority":
                        return getDetailedPriority(row) === filter.value;

                    case "stage":
                        return getCurrentStage(row) === filter.value;

                    case "service":
                        return row.service === filter.value;

                    case "freightStatus":
                        return row.freightStatus === filter.value;

                    case "shipper":
                        return row.shipper === filter.value;

                    case "consignee":
                        return row.consignee === filter.value;

                    case "customer":
                        return row.customer === filter.value;

                    case "shippingLine":
                        return row.shippingLine === filter.value;

                    case "type":
                        return row.type === filter.value;

                    case "docHealth":
                        return getDocumentHealthBucket(row) === filter.value;

                    default:
                        return true;
                }
            })
        );
    };

    const tableRows = useMemo(
        () => applyTableFilter(filteredRows),
        [filteredRows, tableFilters, currency, exchangeRates]
    );

    const kpis = useMemo(() => {
        const total = tableRows.length;
        const totalSelling = tableRows.reduce((s, r) => s + getConvertedSelling(r), 0);
        const totalBuying = tableRows.reduce((s, r) => s + getConvertedBuying(r), 0);
        const totalProfit = totalSelling - totalBuying;

        const avgDocumentHealth = total
            ? Math.round(tableRows.reduce((sum, row) => sum + getDocumentHealth(row), 0) / total)
            : 0;

        return {
            total,
            totalSelling,
            totalBuying,
            totalProfit,
            margin: totalSelling ? (totalProfit / totalSelling) * 100 : 0,
            pendingBookings: tableRows.filter((r) => !r.bookingConfirmationPdf).length,
            departingSoon: tableRows.filter(isDepartingSoon).length,
            arrivingSoon: tableRows.filter(isArrivingSoon).length,
            missingDocuments: tableRows.filter((r) => getMissingDocs(r).length > 0).length,
            demurrageRisk: tableRows.filter((r) => getFreeTimeRisk(r).includes("Demurrage")).length,
            detentionRisk: tableRows.filter((r) => getFreeTimeRisk(r).includes("Detention")).length,
            readyToClose: tableRows.filter((r) => getCurrentStage(r) === "Ready to Close").length,
            negativeMargin: tableRows.filter((r) => getConvertedProfit(r) < 0).length,
            todayActionRequired: tableRows.filter(isActionRequired).length,
            avgDocumentHealth,
        };
    }, [tableRows, currency, exchangeRates]);

    const groupBy = (getter, { limit = 8, sort = true } = {}) => {
        const map = {};

        filteredRows.forEach((row) => {
            const label = getter(row) || "Unknown";
            map[label] = (map[label] || 0) + 1;
        });

        const list = Object.entries(map).map(([label, count]) => ({ label, count }));
        return sort ? list.sort((a, b) => b.count - a.count).slice(0, limit) : list;
    };

    const stageRows = useMemo(
        () =>
            groupBy(getCurrentStage, { sort: false })
                .sort((a, b) => STAGE_ORDER.indexOf(a.label) - STAGE_ORDER.indexOf(b.label))
                .map((d) => ({ ...d, color: toneColor(STAGE_TONES[d.label]) })),
        [filteredRows]
    );

    const breakdownRows = useMemo(() => {
        switch (breakdown) {
            case "priority":
                return ["Critical", "High", "Medium", "Normal"]
                    .map((label) => ({
                        label,
                        count: filteredRows.filter((r) => getDetailedPriority(r) === label).length,
                        color: toneColor(priorityTone(label)),
                    }))
                    .filter((d) => d.count > 0);

            case "docHealth":
                return [
                    ["0-40% Critical", "danger"],
                    ["41-70% Needs Action", "warning"],
                    ["71-99% Almost Complete", "info"],
                    ["100% Complete", "success"],
                ].map(([label, tone]) => ({
                    label,
                    count: filteredRows.filter((r) => getDocumentHealthBucket(r) === label).length,
                    color: toneColor(tone),
                }));

            default:
                return groupBy((row) => row[breakdown]);
        }
    }, [filteredRows, breakdown]);

    const monthlyRows = useMemo(
        () =>
            groupBy((row) => (row.etd ? dayjs(row.etd).format("YYYY-MM") : null), { sort: false })
                .filter((d) => d.label !== "Unknown")
                .sort((a, b) => a.label.localeCompare(b.label))
                .slice(-12),
        [filteredRows]
    );

    const actionItems = [
        { key: "pendingBookings", label: "Pending bookings", description: "No booking confirmation uploaded", icon: CalendarX, tone: "danger", count: kpis.pendingBookings },
        { key: "missingDocuments", label: "Missing documents", description: "At least one required document missing", icon: FileWarning, tone: "warning", count: kpis.missingDocuments },
        { key: "demurrageRisk", label: "Demurrage risk", description: "Free time ends within 2 days", icon: AlertTriangle, tone: "danger", count: kpis.demurrageRisk },
        { key: "detentionRisk", label: "Detention risk", description: "Container return within 2 days", icon: Timer, tone: "danger", count: kpis.detentionRisk },
        { key: "negativeMargin", label: "Negative margin", description: "Buying exceeds selling", icon: TrendingDown, tone: "danger", count: kpis.negativeMargin },
        { key: "readyToClose", label: "Ready to close", description: "POD uploaded, file can be closed", icon: CheckCircle2, tone: "success", count: kpis.readyToClose },
    ].map((item) => ({
        ...item,
        active: isFilterActive(item.key),
        onClick: () => setFilter(item.key, item.key === "negativeMargin" ? true : null),
    }));

    // Önümüzdeki 14 gün içindeki (veya gecikmiş) kalkış/varış hareketleri
    const upcomingMovements = useMemo(() => {
        const events = [];
        const inWindow = (date, from) => {
            const d = dayjs(date).startOf("day").diff(dayjs().startOf("day"), "day");
            return d >= from && d <= 14;
        };

        tableRows.forEach((row) => {
            if (row.etd && !row.actualDeparture && inWindow(row.etd, -3)) {
                events.push({ row, kind: "departure", date: row.etd });
            }
            if (row.eta && !row.actualDelivery && inWindow(row.eta, -3)) {
                events.push({ row, kind: "arrival", date: row.eta });
            }
        });

        return events
            .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
            .slice(0, 6)
            .map(({ row, kind, date }) => {
                const overdue = dayjs(date).isBefore(dayjs(), "day");
                return {
                    key: `${row.id}-${kind}`,
                    icon: kind === "departure" ? Ship : Anchor,
                    tone: overdue ? "danger" : kind === "departure" ? "teal" : "info",
                    title: `${row.hflNo} · ${kind === "departure" ? "Departure" : "Arrival"} ${kind === "departure" ? row.pol : row.pod}`,
                    description: `${row.customer} · ${row.shippingLine}`,
                    when: overdue ? `Overdue · ${relativeDays(date)}` : relativeDays(date),
                    whenColor: overdue ? UI.red : UI.text,
                    date: dayjs(date).format("DD MMM"),
                    onClick: () => clickEditHandler(row.id),
                };
            });
    }, [tableRows]);

    const monthDelta = useMemo(() => {
        const thisMonth = dayjs().format("YYYY-MM");
        const lastMonth = dayjs().subtract(1, "month").format("YYYY-MM");
        const count = (m) => filteredRows.filter((r) => r.etd && dayjs(r.etd).format("YYYY-MM") === m).length;
        return count(thisMonth) - count(lastMonth);
    }, [filteredRows]);

    const health = {
        total: tableRows.length,
        bookingConfirmed: tableRows.filter((r) => hasDoc(r.bookingConfirmationPdf)).length,
        docsComplete: tableRows.filter((r) => getMissingDocs(r).length === 0).length,
        departed: tableRows.filter((r) => r.actualDeparture).length,
        profitable: tableRows.filter((r) => getConvertedProfit(r) > 0).length,
    };

    const linkSx = {
        color: UI.blue,
        cursor: "pointer",
        fontWeight: 600,
        fontSize: 13,
        "&:hover": { textDecoration: "underline" },
    };

    const daysLeft = (base, days) =>
        base && days ? dayjs(base).add(Number(days), "day").diff(dayjs(), "day") : null;

    const getPriorityReasons = (row) => {
        const reasons = [];
        const missingDocs = getMissingDocs(row);
        const freeTimeRisk = getFreeTimeRisk(row);

        if (isDocMissing(row.bookingConfirmationPdf)) reasons.push("Booking confirmation not uploaded");
        if (freeTimeRisk !== "No Risk") reasons.push(freeTimeRisk);
        if (missingDocs.length) {
            reasons.push(`${missingDocs.length} required document${missingDocs.length > 1 ? "s" : ""} missing`);
        }

        return reasons.length ? reasons : ["No open issues on this file"];
    };

    // Detay panelinde gösterilecek aşama / evrak / öncelik özeti
    const buildStatus = (row) => {
        const stage = getCurrentStage(row);
        const selling = getConvertedSelling(row);
        const buying = getConvertedBuying(row);

        const steps = [
            { label: "Booking confirmed", done: hasDoc(row.bookingConfirmationPdf), date: null },
            {
                label: "Departed",
                done: Boolean(row.actualDeparture),
                date: row.actualDeparture
                    ? dayjs(row.actualDeparture).format("DD MMM")
                    : row.etd
                        ? `ETD ${dayjs(row.etd).format("DD MMM")}`
                        : null,
            },
            {
                label: "Delivered",
                done: Boolean(row.actualDelivery),
                date: row.actualDelivery
                    ? dayjs(row.actualDelivery).format("DD MMM")
                    : row.eta
                        ? `ETA ${dayjs(row.eta).format("DD MMM")}`
                        : null,
            },
            { label: "POD uploaded · ready to close", done: hasDoc(row.proofOfDelivery), date: null },
        ];
        const currentIndex = steps.findIndex((step) => !step.done);

        const priority = getDetailedPriority(row);

        return {
            title: row.hflNo,
            subtitle: row.customer,
            priority,
            pills: [
                { label: stage, tone: STAGE_TONES[stage] },
                { label: `${priority} priority`, tone: priorityTone(priority) },
            ],
            facts: [
                ["Origin", row.pol],
                ["Destination", row.pod],
                ["ETD", row.etd ? dayjs(row.etd).format("DD MMM YYYY") : "—"],
                ["ETA", row.eta ? dayjs(row.eta).format("DD MMM YYYY") : "—"],
                [cfg.terms.carrier, row.shippingLine],
                [cfg.terms.vessel, row.vesselName],
            ],
            reasons: getPriorityReasons(row),
            milestones: steps.map((step, i) => ({ ...step, current: i === currentIndex })),
            checklists: [
                {
                    title: "Documents",
                    progress: true,
                    items: [
                        ["Booking Confirmation", row.bookingConfirmationPdf],
                        ["Commercial Invoice", row.commercialInvoice],
                        ["Packing List", row.packingList],
                        ["Export Declaration", row.exportDeclaration],
                        ["Import Declaration", row.importDeclaration],
                    ].map(([name, value]) => ({ name, ok: hasDoc(value) })),
                },
            ],
            freeTime: {
                demurrage: daysLeft(row.eta, row.freeTimeDemurrage),
                detention: daysLeft(row.eta, row.freeTimeDetention),
            },
            financials: { selling, buying, profit: selling - buying },
            openLabel: `Open ${cfg.hfl.prefix} file`,
            onOpen: () => clickEditHandler(row.id),
        };
    };

    const csvColumns = [
        [`${cfg.hfl.prefix} No`, (r) => r.hflNo],
        ["Customer", (r) => r.customer],
        ["Shipper", (r) => r.shipper],
        ["Consignee", (r) => r.consignee],
        ["Origin", (r) => r.pol],
        ["Destination", (r) => r.pod],
        [cfg.terms.carrier, (r) => r.shippingLine],
        [cfg.terms.vessel, (r) => r.vesselName],
        ["ETD", (r) => csvDate(r.etd)],
        ["ETA", (r) => csvDate(r.eta)],
        ["Stage", (r) => getCurrentStage(r)],
        ["Priority", (r) => getDetailedPriority(r)],
        ["Document health %", (r) => getDocumentHealth(r)],
        ["Missing documents", (r) => getMissingDocs(r).join(", ")],
        [`Selling (${currency})`, (r) => csvMoney(getConvertedSelling(r))],
        [`Buying (${currency})`, (r) => csvMoney(getConvertedBuying(r))],
        [`Profit (${currency})`, (r) => csvMoney(getConvertedProfit(r))],
    ];

    const selectedRow = rows.find((r) => r.id === selectedId);

    const fileControlTable = {
        columns: withRawSort([
            { Header: `${cfg.hfl.prefix} No`, accessor: "hflNo" },
            { Header: "Customer", accessor: "customer" },
            { Header: "Origin / Destination", accessor: "route" },
            { Header: "ETA", accessor: "eta" },
            { Header: "Selling", accessor: "selling" },
            { Header: "Buying", accessor: "buying" },
            { Header: "Profit", accessor: "profit" },
            { Header: "Status", accessor: "status" },
        ]),
        rows: tableRows.map((row) => {
            const priority = getDetailedPriority(row);
            const convertedSelling = getConvertedSelling(row);
            const convertedBuying = getConvertedBuying(row);
            const convertedProfit = convertedSelling - convertedBuying;

            return {
                _sort: {
                    hflNo: row.hflNo,
                    customer: row.customer,
                    route: `${row.pol} ${row.pod}`,
                    eta: dateValue(row.eta),
                    selling: convertedSelling,
                    buying: convertedBuying,
                    profit: convertedProfit,
                    status: PRIORITY_RANK[priority],
                },
                hflNo: (
                    <MDTypography component="span" sx={linkSx} onClick={() => clickEditHandler(row.id)}>
                        {row.hflNo}
                    </MDTypography>
                ),
                customer: <TruncatedText text={row.customer} max={40} />,
                route: <StackedCell lines={[["Origin", row.pol], ["Destination", row.pod]]} />,
                eta: row.eta ? dayjs(row.eta).format("DD/MM/YYYY") : "—",
                status: (
                    <DetailsButton
                        priority={priority}
                        hint={`${getCurrentStage(row)} · ${priority} priority`}
                        onClick={() => setSelectedId(row.id)}
                    />
                ),
                selling: formatMoney(convertedSelling, currency),
                buying: formatMoney(convertedBuying, currency),
                profit: (
                    <MDTypography
                        component="span"
                        sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: convertedProfit > 0 ? UI.green : convertedProfit < 0 ? UI.red : UI.muted,
                        }}
                    >
                        {formatMoney(convertedProfit, currency)}
                    </MDTypography>
                ),
            };
        }),
    };

    if (loading) {
        return (
            <MDBox height="50vh" display="flex" alignItems="center" justifyContent="center">
                <CircularProgress />
            </MDBox>
        );
    }

    return (
        <MDBox display="flex" flexDirection="column" gap={2}>
            <DashboardToolbar
                search={search}
                onSearch={setSearch}
                placeholder="Search HFL, customer, shipper, consignee, POL, POD, carrier, container..."
                currency={currency}
                onCurrency={setCurrency}
                filters={tableFilters}
                onRemoveFilter={removeFilter}
                onClear={clearFilter}
                dateRange={dateRange}
                onDateRange={setDateRange}
                dateLabel="ETD"
                ratesMissing={exchangeRates === null}
                resultLabel={`${tableRows.length} files`}
            />

            <KpiGrid>
                <KpiCard
                    label="Open Files"
                    value={kpis.total}
                    icon={Package}
                    tone="neutral"
                    trend={{ delta: monthDelta, label: "ETD this month vs last" }}
                    onClick={clearFilter}
                />
                <KpiCard
                    label="Action Required"
                    value={kpis.todayActionRequired}
                    hint="Risk, missing docs or near ETD/ETA"
                    icon={AlertTriangle}
                    tone="danger"
                    active={isFilterActive("actionRequired")}
                    onClick={() => setFilter("actionRequired")}
                />
                <KpiCard
                    label="Departing ≤ 3 Days"
                    value={kpis.departingSoon}
                    hint="By estimated departure"
                    icon={Ship}
                    tone="teal"
                    active={isFilterActive("departingSoon")}
                    onClick={() => setFilter("departingSoon")}
                />
                <KpiCard
                    label="Arriving ≤ 5 Days"
                    value={kpis.arrivingSoon}
                    hint="By estimated arrival"
                    icon={Anchor}
                    tone="info"
                    active={isFilterActive("arrivingSoon")}
                    onClick={() => setFilter("arrivingSoon")}
                />
                <KpiCard
                    label="Document Health"
                    value={`${kpis.avgDocumentHealth}%`}
                    hint={`${kpis.missingDocuments} files with missing docs`}
                    icon={FileCheck2}
                    tone={kpis.avgDocumentHealth >= 80 ? "success" : "warning"}
                    active={isFilterActive("missingDocuments")}
                    onClick={() => setFilter("missingDocuments")}
                />
            </KpiGrid>

            <Grid container spacing={2}>
                <Grid item xs={12} lg={4}>
                    <Panel title="Shipment Stage" subtitle="Click a stage to filter the table">
                        <DonutWithLegend
                            data={stageRows}
                            totalLabel="files"
                            activeValue={activeFilterValue("stage")}
                            onSelect={(label) => setFilter("stage", label)}
                        />
                    </Panel>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Panel title="Action Center" subtitle="Items needing operational follow-up" bodySx={{ py: 1 }}>
                        <ActionList items={actionItems} />
                    </Panel>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Panel title="Financial Summary" subtitle={`Filtered files · ${currency}`}>
                        <MetricRow label="Selling" value={formatMoney(kpis.totalSelling, currency)} />
                        <MetricRow label="Buying" value={formatMoney(kpis.totalBuying, currency)} />
                        <MetricRow
                            label="Gross Profit"
                            value={formatMoney(kpis.totalProfit, currency)}
                            valueColor={kpis.totalProfit >= 0 ? UI.green : UI.red}
                            strong
                        />
                        <MetricRow
                            label="Margin"
                            value={`${kpis.margin.toFixed(1)}%`}
                            valueColor={kpis.margin >= 0 ? UI.text : UI.red}
                        />
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={7}>
                    <Panel
                        title="Upcoming Movements"
                        subtitle="Departures and arrivals in the next 14 days · click to open the file"
                        bodySx={{ py: 1 }}
                    >
                        <TimelineList items={upcomingMovements} emptyText="No departures or arrivals in the next 14 days" />
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={5}>
                    <Panel title="Operational Health" subtitle="Share of files in view">
                        <MDBox display="flex" flexDirection="column" gap={2}>
                            <ProgressStat
                                label="Booking confirmed"
                                value={health.bookingConfirmed}
                                total={health.total}
                                tone="teal"
                            />
                            <ProgressStat
                                label="Documents complete"
                                value={health.docsComplete}
                                total={health.total}
                                tone={health.docsComplete / (health.total || 1) >= 0.8 ? "success" : "warning"}
                            />
                            <ProgressStat
                                label="Departed"
                                value={health.departed}
                                total={health.total}
                                tone="info"
                            />
                            <ProgressStat
                                label="Profitable"
                                value={health.profitable}
                                total={health.total}
                                tone="success"
                            />
                        </MDBox>
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={7}>
                    <Panel
                        title="Breakdown"
                        subtitle="Top 8 · click a row to filter the table"
                    >
                        <MDBox mb={2}>
                            <SegmentedTabs
                                options={BREAKDOWNS.filter((b) => b.value !== "type" || cfg.hfl.showType).map((b) =>
                                    b.value === "shippingLine" ? { ...b, label: `${cfg.terms.carrier}s` } : b
                                )}
                                value={breakdown}
                                onChange={setBreakdown}
                            />
                        </MDBox>
                        <RankList
                            data={breakdownRows}
                            activeValue={activeFilterValue(breakdown)}
                            onSelect={(label) => setFilter(breakdown, label)}
                        />
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={5}>
                    <Panel title="Monthly Volume" subtitle="HFL files by ETD month (last 12)">
                        <MDBox height={300}>
                            <Bar
                                data={{
                                    labels: monthlyRows.map((x) => dayjs(`${x.label}-01`).format("MMM YY")),
                                    datasets: [
                                        {
                                            label: "HFL files",
                                            data: monthlyRows.map((x) => x.count),
                                            backgroundColor: UI.teal,
                                            borderRadius: 4,
                                            maxBarThickness: 28,
                                        },
                                    ],
                                }}
                                options={barChartOptions}
                            />
                        </MDBox>
                    </Panel>
                </Grid>
            </Grid>

            <TableCard
                cardRef={tableRef}
                onExport={() => downloadCsv(`${cfg.hfl.prefix}-files`, csvColumns, tableRows)}
                title={`${cfg.hfl.prefix} File Control`}
                summary={
                    tableFilters.length
                        ? `${tableRows.length} of ${filteredRows.length} files · ${tableFilters.map((f) => shortLabel(f.label, 40)).join(", ")}`
                        : `All files · ${tableRows.length}`
                }
            >
                <DataTable
                    table={fileControlTable}
                    entriesPerPage={{ defaultValue: 25, entries: [25, 50, 100] }}
                    showTotalEntries
                    isSorted
                    noEndBorder
                />
            </TableCard>

            <FileStatusDrawer
                status={selectedRow ? buildStatus(selectedRow) : null}
                currency={currency}
                onClose={() => setSelectedId(null)}
            />
        </MDBox>
    );
}

export default HflDashboard;
